import React, { useState } from "react";
import styles from "./Notification.module.scss";
import classNames from "classnames";
import { ArrowDownIcon, CheckMarkIcon } from "Assets";
import { useAppDispatch } from "Redux/store";
import { markAsRead } from "Redux/Notifications/reducer";
import {
  INotification,
  NOTIFICATIONS_PRIORITY,
} from "Shared/Types/notifications";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { getNotificationRoute } from "Shared/Constants/notificationsRoute";
import { notificationPresentation } from "./notificationPresentation";
import { displayDate, formatDate } from "Shared/Utils/display";

type NotificationItemProps = {
  notification: INotification;
  onClose?: () => void;
  disabled?: boolean;
};

export const NotificationItem = ({
  notification,
  onClose,
  disabled,
}: NotificationItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const destination = getNotificationRoute(notification);
  const { Icon, label, action, tone } = notificationPresentation(
    notification,
    destination,
  );
  const urgent = notification.priority === NOTIFICATIONS_PRIORITY.URGENT;
  const high = notification.priority === NOTIFICATIONS_PRIORITY.HIGH;
  const arrivalState = {
    notificationArrival: {
      id: notification.id,
      title: notification.title || notification.message,
    },
  };

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    // Keep native browser link behavior for new tabs, windows and context menus.
    if (
      destination &&
      (event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0)
    )
      return;
    event.preventDefault();
    if (pending || disabled) return;
    setError(false);
    if (!notification.is_read) {
      setPending(true);
      try {
        await dispatch(markAsRead({ data: notification.id })).unwrap();
      } catch {
        setError(true);
        setPending(false);
        return;
      }
      setPending(false);
    }
    if (destination) {
      navigate(destination, { state: arrivalState });
      onClose?.();
    }
  };

  const content = (
    <>
      <span className={classNames(styles.typeIcon, styles[tone])}>
        <Icon aria-hidden="true" />
      </span>
      <span className={styles.message}>
        <span className={styles.itemTopline}>
          <span className={styles.typeLabel}>{label}</span>
          {(urgent || high) && (
            <span
              className={classNames(
                styles.priority,
                urgent && styles.priorityUrgent,
              )}
            >
              {urgent ? "Urgent" : "High priority"}
            </span>
          )}
          {!notification.is_read && (
            <span className={styles.unreadDot} aria-label="Unread" />
          )}
        </span>
        <strong className={styles.itemTitle}>
          {notification.title || label}
        </strong>
        {notification.message &&
          notification.message !== notification.title && (
            <span className={styles.description}>{notification.message}</span>
          )}
        <span className={styles.itemFooter}>
          <span className={styles.timestamp}>
            <time
              dateTime={notification.created}
              title={displayDate(notification.created, "dateTime")}
            >
              {formatDate(notification.created, "time")}
            </time>
            {notification.is_read && (
              <span className={styles.readState}>
                <CheckMarkIcon aria-hidden="true" />
                Read
              </span>
            )}
          </span>
          <span className={styles.openLabel}>
            {pending
              ? "Opening…"
              : destination
                ? action
                : notification.is_read
                  ? "Read"
                  : "Mark as read"}
            {destination && <ArrowDownIcon aria-hidden="true" />}
          </span>
        </span>
      </span>
    </>
  );
  const shared = {
    onClick: handleOpen,
    "aria-busy": pending,
    "aria-disabled": pending || disabled || undefined,
    className: classNames(
      styles.notificationItem,
      notification.is_read && styles.notificationItem_read,
    ),
  };
  return (
    <>
      {destination ? (
        <Link {...shared} to={destination} state={arrivalState}>
          {content}
        </Link>
      ) : (
        <button {...shared} type="button" disabled={pending || disabled}>
          {content}
        </button>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          Could not mark this notification as read. Select it again to retry.
        </p>
      )}
    </>
  );
};
